import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { verifyEmail, sendVerificationEmail } from '../utils/auth';
interface VerifyEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  email: string;
}
export function VerifyEmailModal({
  isOpen,
  onClose,
  onSuccess,
  email
}: VerifyEmailModalProps) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const codeInputs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    if (isOpen) {
      setCode(['', '', '', '', '', '']);
      setError('');
      setIsSuccess(false);
      setResendTimer(0);
    }
  }, [isOpen]);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);
  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split('');
      const newCode = [...code];
      pastedCode.forEach((digit, i) => {
        if (index + i < 6) newCode[index + i] = digit;
      });
      setCode(newCode);
      const nextIndex = Math.min(index + pastedCode.length, 5);
      codeInputs.current[nextIndex]?.focus();
      return;
    }
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) {
      codeInputs.current[index + 1]?.focus();
    }
  };
  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputs.current[index - 1]?.focus();
    }
  };
  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const result = await verifyEmail(email, fullCode);
      if (result.success) {
        setIsSuccess(true);
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      } else {
        setError(result.error || 'Invalid code');
      }
    } catch (err) {
      setError('Verification failed');
    } finally {
      setIsLoading(false);
    }
  };
  const handleResend = async () => {
    if (resendTimer > 0) return;
    setIsLoading(true);
    try {
      await sendVerificationEmail(email);
      setResendTimer(60);
      setError('');
    } catch (err) {
      setError('Failed to resend code');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSkip = () => {
    // For demo purposes only
    onSuccess();
    onClose();
  };
  return <AnimatePresence data-id="element-513">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" data-id="element-514" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 p-6 pb-8 max-h-[90vh]" data-id="element-515">
            {/* Header */}
            <div className="flex items-center justify-between mb-6" data-id="element-516">
              <div className="w-10" data-id="element-517" />
              <h2 className="text-lg font-bold text-charcoal" data-id="element-518">Verify Email</h2>
              <button onClick={onClose} className="p-2 -mr-2 hover:bg-sand/30 rounded-full transition-colors" data-id="element-519">
                <X size={24} className="text-charcoal" data-id="element-520" />
              </button>
            </div>

            <AnimatePresence mode="wait" data-id="element-521">
              {!isSuccess ? <motion.div key="form" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="space-y-6" data-id="element-522">
                  <div className="text-center" data-id="element-523">
                    <div className="w-16 h-16 bg-dark-green/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-524">
                      <Mail size={32} className="text-dark-green" data-id="element-525" />
                    </div>
                    <p className="text-charcoal font-medium mb-1" data-id="element-526">
                      Check your inbox
                    </p>
                    <p className="text-sm text-moss-gray" data-id="element-527">
                      We sent a verification code to <br data-id="element-528" />
                      <span className="font-semibold text-charcoal" data-id="element-529">
                        {email}
                      </span>
                    </p>
                  </div>

                  <div className="flex justify-between gap-2" data-id="element-530">
                    {code.map((digit, idx) => <input key={idx} ref={el => codeInputs.current[idx] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleCodeChange(idx, e.target.value)} onKeyDown={e => handleCodeKeyDown(idx, e)} className="w-12 h-14 text-center text-2xl font-bold bg-sand/20 rounded-ios border-2 border-transparent focus:border-dark-green focus:outline-none transition-colors" data-id="element-531" />)}
                  </div>

                  {error && <div className="flex items-center justify-center gap-2 text-red-600" data-id="element-532">
                      <AlertCircle size={16} data-id="element-533" />
                      <span className="text-sm" data-id="element-534">{error}</span>
                    </div>}

                  <div className="space-y-3" data-id="element-535">
                    <Button variant="primary" fullWidth size="lg" loading={isLoading} disabled={code.some(d => !d) || isLoading} onClick={handleVerify} data-id="element-536">
                      Verify Email
                    </Button>

                    <button onClick={handleResend} disabled={resendTimer > 0 || isLoading} className={`w-full flex items-center justify-center gap-2 text-sm font-medium ${resendTimer > 0 ? 'text-moss-gray' : 'text-dark-green hover:underline'}`} data-id="element-537">
                      <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} data-id="element-538" />
                      {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend Code'}
                    </button>
                  </div>

                  <div className="pt-4 border-t border-sand/50 text-center" data-id="element-539">
                    <button onClick={handleSkip} className="text-xs text-moss-gray hover:text-charcoal" data-id="element-540">
                      Skip for demo
                    </button>
                  </div>
                </motion.div> : <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center justify-center text-center py-8" data-id="element-541">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6" data-id="element-542">
                    <CheckCircle size={40} className="text-green-600" data-id="element-543" />
                  </div>
                  <h3 className="text-2xl font-bold text-charcoal mb-2" data-id="element-544">
                    Email Verified!
                  </h3>
                  <p className="text-moss-gray" data-id="element-545">
                    Your account is now fully active.
                  </p>
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </>}
    </AnimatePresence>;
}